import { useState, useEffect, useCallback } from 'react';
import { ref, listAll, getMetadata } from 'firebase/storage';
import { FileVideo, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { storage } from '@/lib/firebase';
import { useJobs } from '@/hooks/useJobs';

interface StoredVideo {
  name: string;
  fullPath: string;
  size: number;
  timeCreated: string;
}

interface VideoPickerProps {
  userId: string;
  onSelect: (filePath: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function displayName(name: string): string {
  return name.replace(/^\d+_/, '');
}

export default function VideoPicker({ userId, onSelect }: VideoPickerProps) {
  const [videos, setVideos] = useState<StoredVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const { deleteVideo } = useJobs();

  const loadVideos = useCallback(async () => {
    setLoading(true);
    try {
      const folderRef = ref(storage, `aicl-object-replacement/videos/${userId}`);
      const result = await listAll(folderRef);

      const items = await Promise.all(
        result.items.map(async (itemRef) => {
          const meta = await getMetadata(itemRef);
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            size: Number(meta.size),
            timeCreated: meta.timeCreated,
          };
        }),
      );

      items.sort((a, b) => new Date(b.timeCreated).getTime() - new Date(a.timeCreated).getTime());
      setVideos(items);
    } catch {
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const handleConfirm = useCallback(() => {
    if (selected) onSelect(selected);
  }, [selected, onSelect]);

  const handleDelete = useCallback(
    async (e: React.MouseEvent, path: string) => {
      e.stopPropagation();
      if (!confirm('Delete this video from storage?')) return;
      try {
        await deleteVideo(path);
        setVideos((prev) => prev.filter((v) => v.fullPath !== path));
        if (selected === path) setSelected(null);
      } catch (err) {
        console.error('Failed to delete video:', err);
      }
    },
    [deleteVideo, selected],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading videos...</span>
      </div>
    );
  }

  if (videos.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-6">No previously uploaded videos found.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{videos.length} video(s) in storage</p>
        <Button variant="ghost" size="sm" onClick={loadVideos}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Refresh
        </Button>
      </div>

      <div className="space-y-1 max-h-64 overflow-y-auto">
        {videos.map((video) => (
          <div
            key={video.fullPath}
            onClick={() => setSelected(video.fullPath)}
            className={cn(
              'flex items-center gap-3 w-full text-left p-3 rounded-lg border transition-colors cursor-pointer group',
              selected === video.fullPath ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50',
            )}
          >
            <FileVideo className="h-5 w-5 flex-shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{displayName(video.name)}</p>
              <p className="text-xs text-muted-foreground">
                {formatBytes(video.size)} &middot; {formatDate(video.timeCreated)}
              </p>
            </div>
            <button
              onClick={(e) => handleDelete(e, video.fullPath)}
              className={cn(
                'opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded',
                'hover:bg-destructive/10 text-muted-foreground hover:text-destructive',
              )}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <Button onClick={handleConfirm} disabled={!selected} className="w-full">
        Use Selected Video
      </Button>
    </div>
  );
}
